import { Origin } from '../interfaces/dropoff-origin.interface';
import { FormCollectionData } from '../interfaces/form-collection-data.interface';
import { Api } from './api.interface';
import { CalendarDay } from './calendar.interface';
import {
  CardPaymentRedirectRequest,
  CardPaymentRedirectResponse,
} from './card.payment.redirect.interface';
import { ServiceOption } from './service-option.interface';

export enum ClaimJourneyStateType {
  START = 'Start',
  QA = 'InProgress',
  BOOKING = 'BookEngineer',
  REBOOKING = 'RebookEngineer',
}

export enum ClaimBundleType {
  REPAIR = 'Repair',
  ANNUAL_SERVICE = 'Service',
}

export interface Claim {
  reflect?: Api.Reflect;
  getData?: Api.GetMandatoryDataResponse;
  created?: boolean;
  accepted?: boolean;
  complete?: boolean;
  rejected?: boolean;
  isRebook?: boolean;
  claimSelection?: ClaimSelection;
  claimID?: string;
  claimReference?: string;
  customer?: CustomerDetails;
  questions?: ClaimQuestion[];
  bookingAvailable?: boolean;
  bookingSelection?: BookingSelection | null;
  bookedServiceOption?: ServiceOptionTypes;
  slotUnavailable?: boolean;
  bookingApiTimeout?: boolean;
  rejectedMessage?: string;
  noBookingDatesFound?: boolean;
  bookingOption?: Api.PutServiceOptionResponse | null;
  serviceOption?: ServiceOption;
  serviceOptions?: Api.GetServiceOptionResponse;
  worldpay?: CardPaymentRedirectResponse;
  bundleToken?: string;
  claimConfirmed?: Api.PutRepairDataResponse | null;
  isGasAppliance?: boolean;
  claimedClientName?: string;
  preClaimStartPageViewed?: PreClaimStartPageViewed;
  dropOffOrigin?: Origin;
  userAddressDetailsUpdated?: ContactDetailsUpdatedStatus;
  userPersonalDetailsUpdated?: ContactDetailsUpdatedStatus;
  isServiceProviderCallback?: boolean;
  modalShownCount?: number;
  callbackQA?: { [key: string]: string };
  callbackWidgetEnabled?: boolean;
  callbackConfirmation?: IWCallbackConfirmation;
  excess?: Payment;
  errorResponse?: boolean;
  isBookingReferenceOnly?: boolean;
  isFullOutage?: boolean;
  isSalesforceOutage?: boolean;
  isGasApplianceNonBoiler?: boolean;
  useBVLookup?: boolean;
}
export interface Payment {
  required?: boolean;
  cost?: string;
  type?: PaymentType;
  request?: CardPaymentRedirectRequest;
}

export enum PaymentType {
  EXCESS = 'EXCESS',
  CALLOUT = 'CALLOUT',
}

export interface IWCallbackConfirmation {
  claimID?: string;
  reference?: string;
  callbackTime?: string;
  description?: string;
  serviceProviderCompanyName?: string;
  serviceProviderTelephone?: string;
  isLandLordOwner?: boolean;
  userDetailsUpdateMessage?: UserUpdateMessage;
  fault?: string;
  faultDate?: string;
  faultAdditionalText?: string;
  customer?: CustomerDetails;
}

export interface UserUpdateMessage {
  message: string | null;
  showCTA: boolean;
}

export interface ContactDetailsUpdatedStatus {
  changed: boolean;
  successfull: boolean;
}

export interface ClaimQuestion {
  question: Api.GetQuestionResponse;
  answer: {
    AnswerID: string | string[];
    AnswerValue: string;
    AnswerType?: string;
  } | null;
}

export interface ClaimSelection {
  request: Partial<Api.UpdateTransactionRequest>;
  selectionState: ClaimSelectionState;
}

export interface ClaimSelectionState {
  modelNumberAlreadyValidated?: boolean;
  PNCNumber?: string;
  MLCode?: string;
  claimStage: ClaimStage;
  faultType?: { id: string; description: string };
  faultTypeAnswer?: { id: string; description: string };
}

export interface BookingSelection {
  date?: string;
  slot?: string;
  collectionData?: FormCollectionData;
  url?: string;
  provider?: Api.ServiceProvider;
  firstAvailableDate?: string;
  identifier?: string;
}

export interface Availability {
  bookingDates: CalendarDay[];
  bookingOption?: Api.PutServiceOptionResponse;
  endDate?: string;
  firstStartDate?: string;
  extraAvailability?: boolean;
  totalAvailability: number;
  blockAvailability: number;
}

export interface CustomerDetails {
  ClaimID: string;
  CustomerFirstName: string;
  CustomerLastName: string;
  CustomerTitle: string;
  CustomersEmail: string;
  CustomersHouseStreetName: string;
  CustomersLandLine: string;
  CustomersLocalArea: string;
  CustomersMobile: string;
  CustomersPostCode: string;
  CustomersTownCity: string;
}

export interface UpdateClaimPayload {
  route?: boolean;
  isASV?: boolean;
  selectionState: ClaimSelection['selectionState'];
  request: Partial<Api.UpdateTransactionRequest>;
  getData?: Api.GetMandatoryDataResponse;
}

export type PartialUpdateClaimPayload = Omit<UpdateClaimPayload, 'claim'>;

export type UpdateClaimSuccessPayload = PartialUpdateClaimPayload & {
  reflect?: Api.Reflect;
  getData?: Api.GetMandatoryDataResponse;
};

export type CreateClaimSuccessPayload = PartialUpdateClaimPayload & {
  ClaimID: string;
  question: Api.GetQuestionResponse;
};

export interface RouteClaimParams {
  claim: Claim;
  manufacturers: string[] | undefined;
  productTypes: string[] | undefined;
  triggeredBy: string;
  isServiceProviderCallback?: boolean;
  complete?: boolean;
  resolvesToSmartFix?: boolean;
}

export enum PreClaimStartPageViewed {
  GasSafety = 0,
  /** @deprecated */
  MaintenanceSignpost = 1,
}

export enum ClaimStage {
  SetOneProductType = '1-SetOneProductType', // if only one product type in list
  AutomaticApplianceId = '2-AutomaticApplianceId', // when model number form bypassed
  ModelNumberSelect = '3-ModelNumberSelect', // in appliance-details
  ApplianceDetailsSubmit = '4-ApplianceDetailsSubmit', // in appliance-details
  ClaimTypeSelect = '5-ClaimTypeSelect', // in repair-details
  CreateClaim = '6-CreateClaim', // in updateClaim$ effect
}

export enum ServiceOptionTypes {
  Manual = 'Manual Referral',
  Home = 'Home Repair',
  Collection = 'Courier',
  SelfSend = 'Self Send',
  DropOff = 'Drop off',
  PayClaim = 'Pay and Claim',
  Callback = 'Callback',
  SelfFit = 'Self Fit',
}

export enum ServiceCollectionData {
  COLLECTION_ADDRESS = 'COLLECTION_ADDRESS',
  DELIVERY_RETURN_ADDRESS = 'DELIVERY_RETURN_ADDRESS',
}

export enum UserStateTypeEnum {
  COMPLETE = 'COMPLETE',
  AUTO_ACC_CONFIRMATION_REQ = 'AUTO_ACC_CONFIRMATION_REQ',
}

export enum UserJourneyTypeEnum {
  QUICKBOOKING = 'quickbooking',
}

export enum BookingReferenceOnlyType {
  BookingReferenceOnly = 'Booking Reference Only',
}
